import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import BackToTop from "@/components/feature/BackToTop";
import Breadcrumb from "@/components/feature/Breadcrumb";
import ArticleCard from "@/components/feature/ArticleCard";
import NotFound from "@/pages/NotFound";
import { useSeo } from "@/hooks/useSeo";
import { articles, trendingList } from "@/mocks/news";

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const article = articles.find((a) => String(a.id) === id);
  const [copied, setCopied] = useState(false);

  useSeo({
    title: article ? `${article.title} - 沸点爆料` : "内容不存在 - 沸点爆料",
    description: article ? article.excerpt : "抱歉，你要查看的内容不存在或已被移除。",
    keywords: article ? [...article.tags, "热点爆料", "热点爆料今日"].join(",") : "热点爆料",
    path: `/article/${id}`,
    ogType: "article",
    jsonLd: article
      ? [
          {
            "@context": "https://schema.org",
            "@type": "NewsArticle",
            headline: article.title,
            description: article.excerpt,
            datePublished: article.date,
            dateModified: article.date,
            articleSection: article.categoryName,
            keywords: article.tags.join(","),
            author: { "@type": "Organization", name: article.author },
            publisher: { "@type": "Organization", name: "沸点爆料" },
            inLanguage: "zh-CN",
          },
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "首页" },
              { "@type": "ListItem", position: 2, name: article.categoryName },
              { "@type": "ListItem", position: 3, name: article.title },
            ],
          },
        ]
      : undefined,
  });

  if (!article) {
    return <NotFound />;
  }

  const related = articles
    .filter((a) => a.categorySlug === article.categorySlug && a.id !== article.id)
    .slice(0, 3);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-background-50">
      <Navbar />

      <main className="flex-1 w-full">
        <div className="w-full px-4 md:px-6 pt-6 md:pt-8">
          <Breadcrumb
            items={[
              { label: "首页", to: "/" },
              { label: article.categoryName, to: `/category/${article.categorySlug}` },
              { label: article.title },
            ]}
          />
        </div>

        <div className="w-full px-4 md:px-6 py-6 md:py-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10">
            <article className="lg:col-span-8">
              <div className="flex flex-wrap items-center gap-2 mb-4">
                <Link
                  to={`/category/${article.categorySlug}`}
                  className="inline-flex items-center h-6 px-2.5 rounded-md bg-primary-500 text-background-50 text-xs font-medium hover:bg-primary-600 transition-colors"
                >
                  {article.categoryName}
                </Link>
                {article.isBreaking && (
                  <span className="inline-flex items-center gap-1 h-6 px-2.5 rounded-md bg-accent-500 text-foreground-950 text-xs font-medium">
                    <span className="w-3.5 h-3.5 flex items-center justify-center">
                      <i className="ri-flashlight-fill"></i>
                    </span>
                    正在跟进
                  </span>
                )}
              </div>

              <h1 className="font-heading text-2xl md:text-4xl font-black text-foreground-950 leading-snug tracking-tight">
                {article.title}
              </h1>

              <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-foreground-500 pb-5 border-b border-background-200">
                <span className="flex items-center gap-1.5">
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-user-line"></i>
                  </span>
                  {article.author}
                </span>
                <span className="flex items-center gap-1.5">
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-time-line"></i>
                  </span>
                  {article.time}
                </span>
                <span className="flex items-center gap-1.5">
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-flight-takeoff-line"></i>
                  </span>
                  阅读约 {article.readTime}
                </span>
                <span className="flex items-center gap-1.5">
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-chat-3-line"></i>
                  </span>
                  {article.comments.toLocaleString("zh-CN")} 条讨论
                </span>
                <button
                  type="button"
                  onClick={handleCopy}
                  className="ml-auto flex items-center gap-1.5 h-8 px-3 rounded-md border border-background-300 text-foreground-600 hover:border-primary-400 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className={copied ? "ri-check-line" : "ri-link"}></i>
                  </span>
                  {copied ? "链接已复制" : "复制链接"}
                </button>
              </div>

              <div className="mt-6 w-full h-56 md:h-96 rounded-lg overflow-hidden bg-background-200">
                <img src={article.image} alt={article.imageAlt} className="w-full h-full object-cover object-top" />
              </div>

              <p className="mt-6 md:mt-7 text-base md:text-lg text-foreground-700 leading-relaxed font-medium">
                {article.excerpt}
              </p>

              <div className="mt-5 flex flex-col gap-4">
                {article.content.map((paragraph, index) => (
                  <p key={index} className="text-base text-foreground-700 leading-8">
                    {paragraph}
                  </p>
                ))}
              </div>

              <div className="mt-7 flex flex-wrap items-center gap-2">
                <span className="text-xs text-foreground-500 mr-1">相关标签</span>
                {article.tags.map((tag) => (
                  <Link
                    key={tag}
                    to={`/category/${article.categorySlug}`}
                    className="inline-flex items-center h-7 px-3 rounded-full bg-secondary-100 text-secondary-900 text-xs hover:bg-secondary-200 transition-colors"
                  >
                    #{tag}
                  </Link>
                ))}
              </div>

              <div className="mt-8 rounded-lg bg-primary-950 p-5 md:p-7 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                  <h2 className="font-heading font-bold text-lg text-background-50">你也有第一手线索？</h2>
                  <p className="mt-1.5 text-sm text-background-300">把你看到的告诉我们，核实后我们会第一时间跟进。</p>
                </div>
                <Link
                  to="/contact"
                  className="shrink-0 inline-flex items-center justify-center gap-2 h-10 px-5 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap"
                >
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-megaphone-line"></i>
                  </span>
                  去爆料
                </Link>
              </div>

              {related.length > 0 && (
                <section aria-label="相关推荐" className="mt-10">
                  <h2 className="font-heading font-bold text-xl text-foreground-950 mb-4">相关推荐</h2>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    {related.map((item) => (
                      <ArticleCard key={item.id} article={item} />
                    ))}
                  </div>
                </section>
              )}
            </article>

            <aside className="lg:col-span-4 flex flex-col gap-6">
              <div className="bg-background-50 border border-background-200 rounded-lg overflow-hidden lg:sticky lg:top-24">
                <h2 className="font-heading font-bold text-base text-foreground-950 px-4 md:px-5 py-4 border-b border-background-200 bg-background-100 flex items-center gap-2">
                  <span className="w-4 h-4 flex items-center justify-center text-primary-600">
                    <i className="ri-fire-fill"></i>
                  </span>
                  今日热榜
                </h2>
                <ol className="divide-y divide-background-200">
                  {trendingList.slice(0, 6).map((item) => (
                    <li key={item.rank}>
                      <Link to={`/article/${item.id}`} className="flex items-start gap-3 px-4 md:px-5 py-3 hover:bg-background-100 transition-colors">
                        <span
                          className={`shrink-0 w-5 h-5 flex items-center justify-center rounded text-xs font-bold ${
                            item.rank <= 3 ? "bg-primary-500 text-background-50" : "bg-background-200 text-foreground-600"
                          }`}
                        >
                          {item.rank}
                        </span>
                        <span className="text-sm text-foreground-800 leading-snug line-clamp-2">{item.title}</span>
                      </Link>
                    </li>
                  ))}
                </ol>
              </div>

              <div className="rounded-lg bg-accent-100 border border-accent-200 p-5">
                <h2 className="font-heading font-bold text-base text-accent-900">持续更新不遗漏</h2>
                <p className="mt-2 text-sm text-accent-900/80 leading-relaxed">
                  重大热点的后续进展会回到原文更新，建议收藏本页，方便随时回来查看最新动态。
                </p>
              </div>
            </aside>
          </div>
        </div>
      </main>

      <Footer />
      <BackToTop />
    </div>
  );
}